import type {
  SocialLink,
  SocialLinksProps,
  SocialLinksSize,
  SocialLinksStyle,
  SocialPlatform,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const STYLE = ["solid", "outline", "plain"] as const;
const SIZE = ["sm", "md", "lg"] as const;
const ALIGN = ["start", "center", "end"] as const;
const PLATFORM = [
  "twitter",
  "x",
  "facebook",
  "instagram",
  "linkedin",
  "youtube",
  "github",
  "tiktok",
  "discord",
  "email",
  "website",
] as const;

function newId(): string {
  return `sl-${Math.random().toString(36).slice(2, 8)}`;
}

export function SocialLinksInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as SocialLinksProps;
  const links = p.links ?? [];

  const setLinks = (next: SocialLink[]) => update(widget.id, { links: next });

  const addLink = () =>
    setLinks([...links, { id: newId(), platform: "website", url: "https://" }]);
  const updateLink = (id: string, patch: Partial<SocialLink>) =>
    setLinks(links.map((lk) => (lk.id === id ? { ...lk, ...patch } : lk)));
  const removeLink = (id: string) => setLinks(links.filter((lk) => lk.id !== id));
  const moveLink = (id: string, dir: "up" | "down") => {
    const idx = links.findIndex((lk) => lk.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= links.length) return;
    const next = links.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setLinks(next);
  };

  return (
    <>
      <SelectField
        label="Style"
        value={p.style ?? "solid"}
        options={STYLE}
        onChange={(v) => update(widget.id, { style: v as SocialLinksStyle })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as SocialLinksSize })}
      />
      <SelectField
        label="Align"
        value={p.align ?? "start"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as SocialLinksProps["align"] })}
      />
      <ColorField
        label="Colour"
        value={p.color ?? "#111827"}
        onChange={(v) => update(widget.id, { color: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Links ({links.length})
      </div>

      {links.map((lk, i) => (
        <div
          key={lk.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveLink(lk.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveLink(lk.id, "down")}
              disabled={i === links.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === links.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeLink(lk.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <SelectField
            label={`Platform ${i + 1}`}
            value={lk.platform}
            options={PLATFORM}
            onChange={(v) => updateLink(lk.id, { platform: v as SocialPlatform })}
          />
          <TextField
            label="URL"
            value={lk.url}
            onChange={(v) => updateLink(lk.id, { url: v })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addLink}
          style={{ width: "100%" }}
        >
          + Add link
        </button>
      </div>
    </>
  );
}
